import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Volume2,
  Mic,
  Monitor,
  Shield,
  Save,
  RotateCcw,
  Palette,
  Gamepad2,
  Bell,
  Globe
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../hooks/useAuthStore'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import toast from 'react-hot-toast/headless'

const SettingsPage = () => {
  const { t, i18n } = useTranslation('settings')
  const { user } = useAuthStore()
  useDocumentTitle(t('title'))
  const [settings, setSettings] = useState({
    audio: {
      masterVolume: 0.8,
      soundEffects: true,
      notifications: true
    },
    display: {
      theme: 'dark',
      showPlayerAvatars: true,
      animationsEnabled: true,
      showNotifications: true,
      chatTimestamps: true,
      compactMode: false
    },
    gameplay: {
      quickJoinPreference: 'any',
      showSkillLevel: true,
      allowSpectators: true
    },
    privacy: {
      showOnlineStatus: true,
      allowFriendRequests: true,
      allowGameInvites: true,
      shareGameHistory: false
    }
  })

  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    // Load user settings from localStorage or API
    const savedSettings = localStorage.getItem(`settings_${user?.id}`)
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings))
      } catch (error) {
        console.error('Failed to load settings:', error)
      }
    }
  }, [user])

  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }))
    setHasChanges(true)
  }

  const saveSettings = () => {
    localStorage.setItem(`settings_${user?.id}`, JSON.stringify(settings))
    setHasChanges(false)
    toast.success('Settings saved successfully!')
  }

  const resetSettings = () => {
    localStorage.removeItem(`settings_${user?.id}`)
    window.location.reload()
  }

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang)
    const languageName = lang === 'en' ? t('language.english') : lang === 'es' ? t('language.spanish') : lang === 'fr' ? t('language.french') : t('language.german')
    toast.success(t('languageChangedTo', { language: languageName }))
  }

  const testAudio = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvGkdBDuY1/PAbz8IJGrK7N2QQAoUXrTt66hVFApGn+DyvGkdBjmY2Pc4LwU')
    audio.volume = settings.audio.masterVolume
    audio.play()
  }

  const SettingSection = ({ title, icon: Icon, children }) => (
    <div className="glass-effect rounded-xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-600 rounded-lg">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-white">{title}</h2>
      </div>
      {children}
    </div>
  )

  const ToggleSetting = ({ label, description, value, onChange }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="text-white font-medium">{label}</div>
        {description && (
          <div className="text-gray-400 text-sm">{description}</div>
        )}
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  )

  const SliderSetting = ({ label, description, value, onChange, min = 0, max = 1, step = 0.1 }) => (
    <div className="py-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-white font-medium">{label}</div>
          {description && (
            <div className="text-gray-400 text-sm">{description}</div>
          )}
        </div>
        <div className="text-gray-300 text-sm">
          {Math.round(value * 100)}%
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
      />
    </div>
  )

  const SelectSetting = ({ label, description, value, onChange, options }) => (
    <div className="py-3">
      <div className="mb-2">
        <div className="text-white font-medium">{label}</div>
        {description && (
          <div className="text-gray-400 text-sm">{description}</div>
        )}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
          <p className="text-gray-400">{t('subtitle')}</p>
        </div>

        <div className="space-y-6">
          {/* Language Settings */}
          <SettingSection title={t('language.title')} icon={Globe}>
            <div className="space-y-4">
              <p className="text-gray-400 text-sm">{t('language.description')}</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`p-3 rounded-lg border-2 transition-colors flex items-center gap-2 ${
                    i18n.language === 'en'
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-gray-700 hover:border-gray-600 text-gray-300'
                  }`}
                >
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded">EN</span>
                  {t('language.english')}
                </button>
                <button
                  onClick={() => changeLanguage('es')}
                  className={`p-3 rounded-lg border-2 transition-colors flex items-center gap-2 ${
                    i18n.language === 'es'
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-gray-700 hover:border-gray-600 text-gray-300'
                  }`}
                >
                  <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">ES</span>
                  {t('language.spanish')}
                </button>
                <button
                  onClick={() => changeLanguage('fr')}
                  className={`p-3 rounded-lg border-2 transition-colors flex items-center gap-2 ${
                    i18n.language === 'fr'
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-gray-700 hover:border-gray-600 text-gray-300'
                  }`}
                >
                  <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">FR</span>
                  {t('language.french')}
                </button>
                <button
                  onClick={() => changeLanguage('de')}
                  className={`p-3 rounded-lg border-2 transition-colors flex items-center gap-2 ${
                    i18n.language === 'de'
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-gray-700 hover:border-gray-600 text-gray-300'
                  }`}
                >
                  <span className="px-2 py-1 bg-yellow-600 text-black text-xs font-bold rounded">DE</span>
                  {t('language.german')}
                </button>
              </div>
            </div>
          </SettingSection>

          {/* Audio Settings */}
          <SettingSection title={t('audio.title')} icon={Volume2}>
            <div className="space-y-4">
              <SliderSetting
                label={t('audio.masterVolume')}
                description={t('audio.masterVolumeDescription')}
                value={settings.audio.masterVolume}
                onChange={(value) => updateSetting('audio', 'masterVolume', value)}
              />
              
              <SliderSetting
                label={t('audio.microphoneVolume')}
                description={t('audio.microphoneVolumeDescription')}
                value={settings.audio.micVolume}
                onChange={(value) => updateSetting('audio', 'micVolume', value)}
              />

              <ToggleSetting
                label={t('audio.soundEffects')}
                description={t('audio.soundEffectsDescription')}
                value={settings.audio.soundEffects}
                onChange={(value) => updateSetting('audio', 'soundEffects', value)}
              />


              <div className="pt-2">
                <button
                  onClick={testAudio}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Test Audio
                </button>
              </div>
            </div>
          </SettingSection>

          {/* Display Settings */}
          <SettingSection title={t('display.title')} icon={Monitor}>
            <div className="space-y-4">
              <SelectSetting
                label={t('display.theme')}
                description={t('display.themeDescription')}
                value={settings.display.theme}
                onChange={(value) => updateSetting('display', 'theme', value)}
                options={[
                  { value: 'dark', label: t('display.dark') },
                  { value: 'light', label: t('display.light') },
                  { value: 'auto', label: 'Auto (System)' }
                ]}
              />

              <ToggleSetting
                label={t('display.showPlayerAvatars')}
                description={t('display.showPlayerAvatarsDescription')}
                value={settings.display.showPlayerAvatars}
                onChange={(value) => updateSetting('display', 'showPlayerAvatars', value)}
              />

              <ToggleSetting
                label={t('display.animations')}
                description={t('display.animationsDescription')}
                value={settings.display.animationsEnabled}
                onChange={(value) => updateSetting('display', 'animationsEnabled', value)}
              />

              <ToggleSetting
                label={t('display.chatTimestamps')}
                description={t('display.chatTimestampsDescription')}
                value={settings.display.chatTimestamps}
                onChange={(value) => updateSetting('display', 'chatTimestamps', value)}
              />

              <ToggleSetting
                label={t('display.compactMode')}
                description={t('display.compactModeDescription')}
                value={settings.display.compactMode}
                onChange={(value) => updateSetting('display', 'compactMode', value)}
              />
            </div>
          </SettingSection>

          {/* Gameplay Settings */}
          <SettingSection title={t('gameplay.title')} icon={Gamepad2}>
            <div className="space-y-4">

              <SelectSetting
                label={t('gameplay.quickJoinPreference')}
                description={t('gameplay.quickJoinPreferenceDescription')}
                value={settings.gameplay.quickJoinPreference}
                onChange={(value) => updateSetting('gameplay', 'quickJoinPreference', value)}
                options={[
                  { value: 'any', label: t('gameplay.anyGameType') },
                  { value: 'fps', label: t('gameplay.fpsGames') },
                  { value: 'strategy', label: t('gameplay.strategyGames') },
                  { value: 'puzzle', label: t('gameplay.puzzleGames') }
                ]}
              />

              <ToggleSetting
                label={t('gameplay.showSkillLevel')}
                description={t('gameplay.showSkillLevelDescription')}
                value={settings.gameplay.showSkillLevel}
                onChange={(value) => updateSetting('gameplay', 'showSkillLevel', value)}
              />

              <ToggleSetting
                label={t('gameplay.allowSpectators')}
                description={t('gameplay.allowSpectatorsDescription')}
                value={settings.gameplay.allowSpectators}
                onChange={(value) => updateSetting('gameplay', 'allowSpectators', value)}
              />
            </div>
          </SettingSection>

          {/* Privacy Settings */}
          <SettingSection title={t('privacy.title')} icon={Shield}>
            <div className="space-y-4">
              <ToggleSetting
                label={t('privacy.showOnlineStatus')}
                description={t('privacy.showOnlineStatusDescription')}
                value={settings.privacy.showOnlineStatus}
                onChange={(value) => updateSetting('privacy', 'showOnlineStatus', value)}
              />

              <ToggleSetting
                label={t('privacy.allowFriendRequests')}
                description={t('privacy.allowFriendRequestsDescription')}
                value={settings.privacy.allowFriendRequests}
                onChange={(value) => updateSetting('privacy', 'allowFriendRequests', value)}
              />

              <ToggleSetting
                label={t('privacy.allowGameInvites')}
                description={t('privacy.allowGameInvitesDescription')}
                value={settings.privacy.allowGameInvites}
                onChange={(value) => updateSetting('privacy', 'allowGameInvites', value)}
              />

              <ToggleSetting
                label={t('privacy.shareGameHistory')}
                description={t('privacy.shareGameHistoryDescription')}
                value={settings.privacy.shareGameHistory}
                onChange={(value) => updateSetting('privacy', 'shareGameHistory', value)}
              />
            </div>
          </SettingSection>

          {/* Save/Reset Actions */}
          <div className="glass-effect rounded-xl p-6">
            <div className="flex gap-4">
              <button
                onClick={saveSettings}
                disabled={!hasChanges}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <Save className="w-5 h-5" />
                {hasChanges ? t('saveChanges') : t('noChanges')}
              </button>
              
              <button
                onClick={resetSettings}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
              >
                <RotateCcw className="w-5 h-5" />
                Reset to Default
              </button>
            </div>
            
            {hasChanges && (
              <p className="text-yellow-400 text-sm mt-2 text-center">
                You have unsaved changes
              </p>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
        }
        
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  )
}

export default SettingsPage